import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Alert, Card, Tag, Tooltip, Progress, Empty, Spin, Button, Tabs, Grid, theme } from 'antd';
import {
  Workflow, Boxes, GitBranch, FileCheck2,
  RefreshCw, Printer, Maximize2, Minimize2, ClipboardCheck, BadgeCheck, FileSignature,
} from 'lucide-react';
import { PageRail, useFullscreen } from '@/components/PageChrome';
import { accessControlUtils } from '@/components/AccessControlUtils';
import Icons from '@/components/third-party/Icons';
import DataTable from '@/components/DataTable';
import PageHeader from '@/components/PageHeader';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import { get_batch_process_list } from '@/utils/api';
import { resolveOperationExecutionStatus } from '@/utils/operationExecutionStatus';
import { fmt } from './helpers';
import StatusIndicator from '@/components/genealogy/StatusIndicator';
import BatchStatusCell from '@/components/batch/BatchStatusCell';
import BatchStatusBanner from '@/components/batch/BatchStatusBanner';
import BatchRecordView from '@/components/batch/BatchRecordView';
import BatchBomPanel from '@/components/BatchBomPanel';
import RecordExportButton from '@/components/RecordExportButton';
import BatchOrderHeader from '@/components/BatchOrderHeader';                     // order info + expiry
import FullTraceCard from '@/components/FullTraceCard';
import ProcessOperationsModal from './ProcessOperationsModal';
import CleaningGateStrip from '@/components/CleaningGateStrip';
import PendingSignOffStrip from '@/components/PendingSignOffStrip';

/**
 * ux-production-supervisor #10a: the payload sends a clock string ("01:17:30") and the column
 * appended " min" to it, so the shop floor read "01:17:30 min" — an hour and a quarter presented
 * as seventy-eight thousand minutes. Accept both shapes the API can send and say the duration the
 * way a supervisor says it out loud.
 */
export const fmtDuration = (v) => {
  if (v == null || v === '') return '—';
  const s = String(v).trim();
  const clock = s.match(/^(\d+):([0-5]\d):([0-5]\d)(?:\.\d+)?$/);
  if (clock) {
    const h = Number(clock[1]);
    const m = Number(clock[2]);
    if (h === 0 && m === 0) return `${Number(clock[3])} sec`;
    return h > 0 ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`;
  }
  const mins = Number(s);
  if (!Number.isFinite(mins)) return s;
  if (mins < 60) return `${Math.round(mins)} min`;
  return `${Math.floor(mins / 60)} h ${String(Math.round(mins % 60)).padStart(2, '0')} min`;
};

/** Batch_Size_Info.batch_status words that mean the batch is past "can it start?" — so a completed batch
 *  with a null start_datetime never reads "clear to start". */
const STARTED_STATUSES = new Set(['running', 'in progress', 'hold', 'completed', 'released', 'terminated', 'rejected', 'cancelled']);

/** EXV-021: what the deviation count is — the review register's items, from its three sources. */
const DEVIATIONS_TOOLTIP =
  'From the review register: legacy deviation records (parameter, ingredient, sampling, duration), IPC failures and OOS results, and runner step deviations.';

/** Deep-linkable tabs — `?tab=` survives a reload and can be linked from the cockpit / insights. */
const DEFAULT_TAB = 'overview';
const TAB_KEYS = ['overview', 'processes', 'bom', 'trace', 'record'];

// ==============================|| BATCH PROCESS DETAILS ||============================== //

const BatchProcessDetails = () => {
  const { batch_number } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const narrow = !screens.md;          // under 768 px the process table becomes cards
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const axisColor = isDark ? '#94a3b8' : '#64748b';

  const [batchData, setBatchData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();

  // Same module keys the menu gates these pages on (menu-items/dashboard.js), so the rail never offers
  // a link that lands on an access-denied page.
  const moduleView = useSelector((state) => state.moduleView);
  const recordKey = moduleView === 'prod' ? 'BMR' : 'BPR';
  const canRead = (...keys) => keys.some((k) => accessControlUtils(k)?.read_access);

  const [params] = useSearchParams();
  const activeTab = TAB_KEYS.includes(params.get('tab')) ? params.get('tab') : DEFAULT_TAB;
  const goToTab = useCallback((key) => {
    const next = new URLSearchParams(params);
    if (key === DEFAULT_TAB) next.delete('tab'); else next.set('tab', key);
    const qs = next.toString();
    navigate(`/batch-process-details/${encodeURIComponent(batch_number)}${qs ? `?${qs}` : ''}`, { replace: true });
  }, [navigate, params, batch_number]);

  const fetchBatchDetails = () => {
    setLoading(true);
    get_batch_process_list({ batch_number }, (res) => {
      setBatchData(res?.data || null);
      setLoadError(res?.error ? { status: res.status, message: res.message } : null);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (batch_number) fetchBatchDetails();
  }, [batch_number]);

  const processes = batchData?.processes || [];
  const totalProcesses = processes.length;
  // ux-gmp-auditor #3: a batch-scope OOS carries no operation, so it cannot appear in any process row.
  // The batch totals are stated separately rather than being silently dropped from the rollup.
  const exceptions = batchData?.exceptions_summary || null;

  // Widths are pixels and add up to a little under a 1440 px content column, so the table fits without a
  // horizontal scrollbar on a desktop and scrolls by only a couple of columns on a 1100 px laptop.
  // The dispensing Yes/No column is gone — the flag is derived from the operation types and is only
  // editable on the process form, so its 122 px came out of the budget rather than being reassigned.
  const columns = [
    { title: 'Process Code', dataIndex: 'process_code', key: 'process_code', width: 136, render: (v) => v || '—' },
    {
      title: 'Process Name',
      dataIndex: 'process_name',
      key: 'process_name',
      width: 178,
      render: (v) => (
        <Tooltip title={v}>
          <div className="truncate" style={{ maxWidth: '100%' }}>{v || '—'}</div>
        </Tooltip>
      ),
    },
    { title: 'Duration', dataIndex: 'duration', key: 'duration', width: 88, render: fmtDuration },
    {
      title: 'Execution Status',
      dataIndex: 'execution_status',
      key: 'execution_status',
      width: 118,
      render: (v) => {
        const resolved = resolveOperationExecutionStatus(v);
        return <Tag color={resolved.style.tagColor}>{resolved.label}</Tag>;
      },
    },
    {
      // EXV-025: when the process actually ran; the schedule only while nothing has started
      title: 'Run',
      key: 'run_window',
      width: 150,
      render: (_, r) => {
        if (r.actual_start) {
          return (
            <div style={{ fontSize: 12 }}>
              {fmt(r.actual_start)} → {r.actual_end ? fmt(r.actual_end) : 'not ended'}
            </div>
          );
        }
        const scheduledFor = r.schedule?.schedule_dt;
        if (scheduledFor) {
          return <span style={{ fontSize: 12 }}>Scheduled for {fmt(scheduledFor)}</span>;
        }
        return <span style={{ fontSize: 12, color: axisColor }}>Not scheduled</span>;
      },
    },
    {
      title: 'Operations',
      key: 'operations_summary',
      width: 142,
      render: (_, r) => {
        const total = r.operations_summary?.total || 0;
        const completed = r.operations_summary?.completed || 0;
        const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
        return (
          <div style={{ width: '100%' }}>
            <Progress percent={pct} size="small" status={pct === 100 ? 'success' : 'active'} />
            <div style={{ fontSize: 11, color: axisColor }}>
              {completed}/{total} completed
              {r.operations_summary?.pending ? ` · ${r.operations_summary.pending} pending` : ''}
            </div>
          </div>
        );
      },
    },
    {
      // EXV-021: the server counts the review register's deviation items (services/execution/deviation_counts.py),
      // runner step deviations included — the same number the audit trail and the BMR book state.
      title: 'Deviations',
      dataIndex: 'deviations_total',
      key: 'deviations_total',
      width: 150,
      align: 'center',
      render: (v) => (
        <StatusIndicator
          state={v ? 'warning' : 'neutral'}
          label={String(v || 0)}
          tooltip={DEVIATIONS_TOOLTIP}
        />
      ),
    },
    {
      // Deliberately a different name and a different number: the release gate counts every open
      // attempt, so one parameter corrected four times contributes four here and one deviation above.
      title: 'Open IPC/OOS',
      dataIndex: 'open_events_total',
      key: 'open_events_total',
      width: 104,
      align: 'center',
      render: (v) => (
        <StatusIndicator
          state={v ? 'warning' : 'neutral'}
          label={String(v || 0)}
          tooltip="In-process-control and OOS events on this process that are still open — every correction attempt counts separately. This is the number the QA release gate blocks on; it is not the deviation count."
        />
      ),
    },
    {
      title: 'Details',
      key: 'action',
      width: 72,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Tooltip title="View Operations">
          <Button
            type="text"
            size="small"
            aria-label={`View operations of ${record.process_name || record.process_code || 'this process'}`}
            icon={<Icons.Eye className="w-5 h-5" style={{ color: defaultColors.primary }} />}
            onClick={() => setSelectedProcess(record)}
          />
        </Tooltip>
      ),
    },
  ];

  // Page-level actions live on the shared right rail. The hero's old quick-link buttons (BOM,
  // Traceability, Certified record) only switched the tabs below, so they are gone rather than moved
  // (B360-01 A-12). Links go to pages that open on this batch; each is gated by the menu's own keys.
  // Not here on purpose: /batch-traceability (its picker ignores ?batch=) and the executed BMR report
  // (no route takes a batch number). Links show only once the batch has loaded. Sign / acknowledge /
  // export-certified-copy stay where they are.
  const bn = encodeURIComponent(batch_number || '');
  const railItems = [
    { key: 'refresh', icon: RefreshCw, label: 'Refresh batch', onClick: fetchBatchDetails, loading },
    { key: 'print', icon: Printer, label: 'Print this screen (not the certified batch record)', onClick: () => window.print(), disabled: !batchData },
    {
      key: 'fullscreen',
      icon: isFullscreen ? Minimize2 : Maximize2,
      label: isFullscreen ? 'Exit full screen' : 'Full screen',
      onClick: toggleFullscreen,
      active: isFullscreen,
    },
    batchData && canRead(`${recordKey} Approval`) && {
      key: 'rbe',
      icon: ClipboardCheck,
      label: 'Open in Review by Exception',
      onClick: () => navigate(`/qa-review-dashboard?batch_number=${bn}`),
    },
    batchData && canRead(`${recordKey} Approval`, 'Quality Management') && {
      key: 'release',
      icon: BadgeCheck,
      label: 'Open Batch Release',
      onClick: () => navigate(`/batch-workflow?batch_number=${bn}`),
    },
    batchData && canRead('Batch Genealogy', 'Quality Management', 'Batch Traceability') && {
      key: 'genealogy',
      icon: GitBranch,
      label: 'Open Batch Genealogy',
      onClick: () => navigate(`/batch-genealogy-full?batch=${bn}`),
    },
    batchData && canRead('Quality Management', `${recordKey} Approval`, 'Audit Logs') && {
      key: 'record-exports',
      icon: FileSignature,
      label: 'Certified record exports for this batch',
      onClick: () => navigate(`/record-exports?batch=${bn}`),
    },
  ];

  // antd only mounts a pane the first time it becomes active, so the BOM / trace requests fire on first open.
  const tabItems = [
    {
      key: 'overview',
      label: <span style={{ color: textColor, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icons.ClipboardList size={14} />Overview</span>,
      children: (
        <>
          {/* N1 — the cleaning gate, on the batch header. A fail-closed gate now refuses to start
              an operation on equipment that is not clean to the grade this batch needs; without
              this strip the first anyone hears of it is a 409 on an operator's tablet. */}
          {/* EXV-024: an ended batch (end time set, or the server's ended word) can never be refused again. */}
          {!batchData?.end_datetime && !batchData?.gate?.ended && (
            <CleaningGateStrip batchNumber={batch_number} started={!!batchData?.start_datetime || STARTED_STATUSES.has(String(batchData?.batch_status || '').trim().toLowerCase())} />
          )}

          {/* Order metadata + expiry (BatchOrderInfo / Batch_Size_Info.batch_expiry_date) */}
          <BatchOrderHeader batchNumber={batch_number} />
        </>
      ),
    },
    {
      key: 'processes',
      label: <span style={{ color: textColor, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Workflow size={14} />Processes ({totalProcesses})</span>,
      children: (
        <Card size="small" styles={{ body: { padding: 8 } }}>
          {exceptions && (
            <div style={{ fontSize: 12, color: axisColor, padding: '2px 4px 8px' }}>
              {/* EXV-021: the review register's count — legacy records, IPC / OOS and runner step deviations. */}
              <StatusIndicator
                state={exceptions.deviations_total ? 'warning' : 'neutral'}
                label={`Deviations: ${exceptions.deviations_total}`}
                tooltip={DEVIATIONS_TOOLTIP}
                strong
              />
              <StatusIndicator
                state={exceptions.open_events_total ? 'warning' : 'neutral'}
                label={`Open IPC/OOS events: ${exceptions.open_events_total}`}
              />
              {exceptions.batch_level_deviations || exceptions.batch_level_open_events ? (
                <Tooltip title="Recorded against the batch rather than a single operation (a batch-scope OOS has no operation), so they are not in any row below.">
                  <span style={{ cursor: 'help' }}>
                    {' '}· {exceptions.batch_level_deviations} deviation
                    {exceptions.batch_level_deviations === 1 ? '' : 's'} and{' '}
                    {exceptions.batch_level_open_events} open event
                    {exceptions.batch_level_open_events === 1 ? '' : 's'} are batch-level
                  </span>
                </Tooltip>
              ) : null}
            </div>
          )}
          {narrow ? (
            processes.length === 0 ? (
              <Empty description="No processes found for this batch" />
            ) : (
              <div>
                {processes.map((p, i) => {
                  const total = p.operations_summary?.total || 0;
                  const completed = p.operations_summary?.completed || 0;
                  const resolved = resolveOperationExecutionStatus(p.execution_status);
                  return (
                    <Card key={p.process_id ?? i} size="small" style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: token.colorText, flex: '1 1 160px', minWidth: 0, overflowWrap: 'anywhere' }}>
                          {p.process_name || p.process_code || '—'}
                        </span>
                        <Tag color={resolved.style.tagColor}>{resolved.label}</Tag>
                      </div>
                      <div style={{ fontSize: 12, color: axisColor, marginTop: 4 }}>
                        {columns.find((c) => c.key === 'run_window').render(null, p)}
                      </div>
                      <div style={{ fontSize: 12, color: axisColor, marginTop: 2 }}>
                        {completed}/{total} operations completed · {fmtDuration(p.duration)}
                      </div>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
                        <StatusIndicator state={p.deviations_total ? 'warning' : 'neutral'} label={`Deviations: ${p.deviations_total || 0}`} tooltip={DEVIATIONS_TOOLTIP} />
                        <StatusIndicator state={p.open_events_total ? 'warning' : 'neutral'} label={`Open IPC/OOS: ${p.open_events_total || 0}`} />
                        <Button size="small" onClick={() => setSelectedProcess(p)} style={{ marginInlineStart: 'auto' }}>
                          View operations
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )
          ) : (
          <DataTable
            tableKey="batchProcessDetailsProcesses"
            searchable
            searchPlaceholder="Search processes…"
            columnConfig
            showCount={false}
            bordered
            data={processes.map((p, i) => ({ ...p, key: p.process_id ?? i }))}
            columns={columns}
            // the page card scrolls; the table just takes the height its rows need
            height={null}
            minHeight={320}
            pagination={{ pageSize: 15 }}
            locale={{ emptyText: <Empty description="No processes found for this batch" /> }}
          />
          )}
        </Card>
      ),
    },
    {
      key: 'bom',
      label: <span style={{ color: textColor, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Boxes size={14} />Bill of materials</span>,
      children: (
        <Card size="small" styles={{ body: { padding: 8 } }}>
          <BatchBomPanel batchNumber={batch_number} />
        </Card>
      ),
    },
    {
      key: 'trace',
      label: <span style={{ color: textColor, display: 'inline-flex', alignItems: 'center', gap: 6 }}><GitBranch size={14} />Traceability</span>,
      children: <FullTraceCard batchNumber={batch_number} />,
    },
    {
      key: 'record',
      label: <span style={{ color: textColor, display: 'inline-flex', alignItems: 'center', gap: 6 }}><FileCheck2 size={14} />Record &amp; signatures</span>,
      children: (
        <>
          {/* Certified electronic record — signed package (BMR book PDF, record.json, audit-trail CSV, SHA-256 manifest) */}
          <Card size="small" styles={{ body: { padding: 8 } }} title={<span style={{ fontSize: 13 }}>Certified electronic record</span>}>
            <RecordExportButton batchNumber={batch_number} />
          </Card>
          {/* EXV-019 / EXV-020: the executed record per operation and the signature register, from the runner */}
          <BatchRecordView batchNumber={batch_number} />
        </>
      ),
    },
  ];

  return (
    <div
      className="p-2.5 pt-5"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
    <Card
      className="border border-gray-300 shadow-md"
      bordered={false}
      classNames={{ body: 'page-card-scroll' }}
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff', height: 'calc(100dvh - 120px)', display: 'flex', flexDirection: 'column' }}
      styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
      title={
        <PageHeader
          compact
          icon={<Icons.ClipboardList size={22} />}
          title={batch_number}
          meta={batchData ? <BatchStatusCell record={batchData} showBanner={false} /> : null}
          subtitle={batchData?.recipe?.name || undefined}
          description={batchData ? `${fmt(batchData.start_datetime)} → ${batchData.end_datetime ? fmt(batchData.end_datetime) : 'not ended'}` : undefined}
        />
      }
    >
      <PageRail items={railItems} />
      {loading ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin size="large" tip="Loading batch details…" />
        </div>
      ) : loadError ? (
        <Alert
          type={loadError.status === 404 ? 'warning' : 'error'}
          showIcon
          style={{ margin: 24 }}
          message={loadError.status === 404 ? 'Batch not found in this plant' : 'Could not load this batch'}
          description={loadError.message}
          action={<Button size="small" icon={<RefreshCw size={13} />} onClick={fetchBatchDetails}>Retry</Button>}
        />
      ) : !batchData ? (
        <Empty description="No details found for this batch" style={{ padding: 48 }} />
      ) : (
        <>
          {/* EXV-003: the gate's facts — hold (who, when, why), issue approval waited on, ended. */}
          <BatchStatusBanner gate={batchData.gate} statusWord={batchData.status_word} style={{ marginBottom: 12 }} />

          {/* #1 — the counter-signature, where the batch is. Every route that should have led to
              the sign-off queue dead-ended; the batch page is where the supervisor actually lands. */}
          <PendingSignOffStrip batchNumber={batch_number} onChange={fetchBatchDetails} />

          {/* Order details, the cleaning gate, processes, BOM, trace and the record each have their own tab. The gate banner and the sign-off strip stay above because they ask someone to act. */}
          <Tabs
            activeKey={activeTab}
            onChange={goToTab}
            size="large"
            className={isDark ? 'dark-tabs' : ''}
            style={{ marginTop: 12 }}
            tabBarStyle={{ backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f9f9f9', borderRadius: '8px', padding: '2px 8px', marginBottom: 8 }}
            items={tabItems}
          />
        </>
      )}

      <ProcessOperationsModal
        open={!!selectedProcess}
        onClose={() => setSelectedProcess(null)}
        batchNumber={batch_number}
        process={selectedProcess}
      />
    </Card>
    </div>
  );
};

export default BatchProcessDetails;
